// src/pages/Admin/Views/SalesTools/SalesProspectCalculator.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toBlob } from 'html-to-image';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Slider } from "../../../../components/ui/slider";
import { Label } from "../../../../components/ui/label";
import { useToast } from "../../../../hooks/use-toast";
import { getCategoryPricingConfig } from '../../../../services/AdminApis';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import LocaleSelector from '../../../../components/Shared/LocaleSelector'; //
import { useAdminStore } from '../../../../store/adminStore'; //
import { Loader2, Calculator, Coins, AlertCircle, Copy } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { Separator } from "../../../../components/ui/separator";

// Helper: Format Currency
const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
};

const PriceRow = ({ label, value, isBold = false, className = "" }) => (
    <div className={`flex justify-between items-center text-sm gap-2 ${isBold ? 'font-semibold' : ''} ${className}`}>
        <span className="text-muted-foreground whitespace-nowrap">{label}:</span>
        <span className="text-right break-words">{value}</span>
    </div>
);

const SalesProspectCalculator = () => {
    // --- Store Access ---
    const locale = useAdminStore((state) => state.locale); // Get selected country
    const setLocale = useAdminStore((state) => state.setLocale);

    // --- URL params (deep link: /prospect-calculator/:countryCode?/:categoryId?) ---
    const { countryCode: urlCountryCode, categoryId: urlCategoryId } = useParams();

    // --- State ---
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [config, setConfig] = useState(null);
    const [discount, setDiscount] = useState(0);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const resultCardRef = useRef(null);
    const { toast } = useToast();

    // Reset config if the locale changes (so we don't show INR prices for a UAE category)
    useEffect(() => {
        setSelectedCategory(null);
        setConfig(null);
        setDiscount(0);
    }, [locale]);

    // Deep link: sync the URL's country into the store.
    // Country codes are stored/selected in uppercase (matches LocaleSelector's
    // dropdown values), but URL segments follow this app's lowercase locale
    // convention (e.g. /categories/manage/in/...), so normalize on the way in.
    const urlLocale = urlCountryCode ? urlCountryCode.toUpperCase() : null;
    useEffect(() => {
        if (urlLocale && urlLocale !== locale) {
            setLocale(urlLocale);
        }
    }, [urlLocale]);

    // Deep link: once the store's locale matches the URL's country, auto-fetch the URL's category
    useEffect(() => {
        if (urlCategoryId && urlLocale && locale === urlLocale) {
            handleCategorySelect({ id: urlCategoryId });
        }
    }, [urlCategoryId, urlLocale, locale]);

    // Fetch Config when a category is selected via the component
    const handleCategorySelect = async (cat) => {
        setSelectedCategory(cat);
        setLoadingConfig(true);
        setDiscount(0); // Reset discount on new selection

        try {
            const data = await getCategoryPricingConfig(cat.id);
            setConfig(data);
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to load pricing.";
            toast({ variant: "destructive", title: "Error", description: msg });
            setConfig(null);
        } finally {
            setLoadingConfig(false);
        }
    };

    // Copy the results card as an image (matches the pattern used in AdminSpecialSetCreator)
    const copyResultAsImage = async () => {
        if (!resultCardRef.current) return;

        if (!navigator.clipboard?.write || !window.ClipboardItem) {
            toast({
                variant: "destructive",
                title: "Copy Not Supported",
                description: "This browser does not support copying images to the clipboard.",
            });
            return;
        }

        try {
            setIsCopying(true);

            const captureNode = resultCardRef.current;
            const captureWidth = Math.ceil(captureNode.scrollWidth);
            const captureHeight = Math.ceil(captureNode.scrollHeight) + 40;

            const imageBlob = await toBlob(captureNode, {
                backgroundColor: '#ffffff',
                width: captureWidth,
                height: captureHeight,
                pixelRatio: 2,
                cacheBust: true,
                style: {
                    position: 'static',
                    left: '0',
                    top: '0',
                    overflow: 'visible',
                },
            }).then((blob) => {
                if (!blob) {
                    throw new Error('Could not create an image of the results card.');
                }
                return blob;
            });

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': imageBlob }),
            ]);

            toast({
                title: "Copied as Image",
                description: "The pricing breakdown is ready to paste from your clipboard.",
            });
        } catch (copyError) {
            console.error('Could not copy results card as an image:', copyError);
            toast({
                variant: "destructive",
                title: "Copy Failed",
                description: "Could not copy the results card. Use HTTPS or localhost and try again.",
            });
        } finally {
            setIsCopying(false);
        }
    };

    // --- Calculation Logic ---
    const calculations = useMemo(() => {
        if (!config) return null;

        const WELCOME_SET_LEADS = 10;
        const totalCoins = config.base_price_coins * WELCOME_SET_LEADS; //
        
        // Base Cost (Ex Tax)
        const baseCostExTax = totalCoins * config.base_rate; //
        
        // Discount
        const discountAmount = baseCostExTax * (discount / 100);
        const costAfterDiscountExTax = baseCostExTax - discountAmount;
        
        // Tax
        const taxAmount = costAfterDiscountExTax * (config.tax_rate / 100); //
        const finalPayable = costAfterDiscountExTax + taxAmount;

        // Effective Cost Per Lead
        const effectiveCostPerLead = costAfterDiscountExTax / WELCOME_SET_LEADS;

        return {
            leadsCount: WELCOME_SET_LEADS,
            totalCoins,
            baseCostExTax,
            discountAmount,
            costAfterDiscountExTax,
            taxAmount,
            finalPayable,
            effectiveCostPerLead
        };
    }, [config, discount]);

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6 md:space-y-8">
            <div className="flex flex-col gap-1">
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 text-foreground">
                    <Calculator className="h-5 w-5 sm:h-6 sm:w-6 text-brand-purple flex-shrink-0" />
                    <span>Sales Prospect Calculator</span>
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                    Calculate <strong>Welcome Set (First 10 Leads)</strong> costs for prospective sellers.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                {/* --- Left Column: Inputs --- */}
                <div className="lg:col-span-4 space-y-6">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex justify-between items-center">
                                1. Select Region & Category
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Locale Selector */}
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1.5 block">Region / Country</Label>
                                <LocaleSelector className="w-full bg-white border-gray-200" />
                            </div>

                            <Separator />

                            {/* Category Search */}
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1.5 block">Service Category</Label>
                                <div className="w-full">
                                    <CategorySearch
                                        key={locale} // Force re-render on locale change
                                        locale={locale} // Pass store locale to search
                                        onSelect={handleCategorySelect}
                                        placeholder={`Search ${locale ? locale.toUpperCase() : ''} categories...`}
                                        typeFilter="child"
                                        initialQuery={config?.category_name || ''}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={!config ? "opacity-50 pointer-events-none transition-opacity" : "transition-opacity"}>
                        <CardHeader>
                            <CardTitle className="text-base">2. Apply Discount</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <Label>Discount %</Label>
                                <span className="text-xl sm:text-2xl font-bold text-blue-600">{discount}%</span>
                            </div>
                            <Slider
                                min={0}
                                max={50}
                                step={1}
                                value={[discount]}
                                onValueChange={(val) => setDiscount(val[0])}
                                className="w-full"
                            />
                            {discount > 30 && (
                                <Alert className="bg-amber-50 text-amber-800 border-amber-200 py-2">
                                    <AlertCircle className="h-4 w-4 shrink-0" />
                                    <div className="flex flex-col gap-1">
                                        <AlertTitle className="text-xs font-bold leading-none">High Discount!</AlertTitle>
                                        <AlertDescription className="text-xs">
                                            Avoid discounts {'>'} 30%.
                                        </AlertDescription>
                                    </div>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* --- Right Column: Results --- */}
                <div className="lg:col-span-8">
                    {loadingConfig && (
                        <div className="h-64 flex items-center justify-center border rounded-lg bg-slate-50">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    )}

                    {!loadingConfig && !config && (
                        <div className="h-64 flex flex-col items-center justify-center border rounded-lg border-dashed bg-slate-50 text-center p-6">
                            <div className="bg-slate-200 p-3 rounded-full mb-3">
                                <Calculator className="h-6 w-6 text-slate-500" />
                            </div>
                            <h3 className="font-semibold text-slate-700 text-sm sm:text-base">Ready to Calculate</h3>
                            <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-xs">
                                Select a region and search for a category to view the Welcome Set pricing breakdown.
                            </p>
                        </div>
                    )}

                    {!loadingConfig && config && calculations && (
                        <div className="space-y-2">
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={copyResultAsImage}
                                    disabled={isCopying}
                                >
                                    {isCopying ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Copy className="mr-2 h-4 w-4" />
                                    )}
                                    {isCopying ? 'Copying...' : 'Copy as Image'}
                                </Button>
                            </div>
                            <Card ref={resultCardRef} className="shadow-lg border-t-4 border-t-blue-600 overflow-hidden">
                            <CardHeader className="bg-slate-50/50 pb-6 border-b">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
                                    <div className="w-full sm:w-auto break-words">
                                        <CardTitle className="text-lg sm:text-xl text-blue-900">Welcome Set Offer</CardTitle>
                                        <CardDescription className="text-sm sm:text-base mt-1 flex flex-wrap items-center gap-x-2">
                                            <span>Category: <strong className="text-slate-900 font-semibold">{config.category_name}</strong></span>
                                            <span className="hidden sm:inline text-muted-foreground">•</span>
                                            <span>Region: <strong className="text-slate-900 font-semibold">{config.country_code}</strong></span>
                                        </CardDescription>
                                    </div>
                                    <div className="text-left sm:text-right w-full sm:w-auto bg-blue-50/50 sm:bg-transparent p-3 sm:p-0 rounded-md sm:rounded-none">
                                        <div className="flex items-center justify-start sm:justify-end gap-1.5 text-blue-600 font-bold text-2xl sm:text-3xl">
                                            <Coins className="h-5 w-5 sm:h-6 sm:w-6" />
                                            {calculations.totalCoins}
                                        </div>
                                        <span className="text-xs sm:text-sm text-muted-foreground block mt-0.5">Total Coins (10 Leads)</span>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                                {/* Configuration */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Set Details</h4>
                                    <div className="bg-slate-50 p-4 rounded-lg space-y-3 text-xs sm:text-sm border">
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="text-muted-foreground">Leads in Set:</span>
                                            <span className="font-medium text-right">10 Leads</span>
                                        </div>
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="text-muted-foreground">Base Coins per Lead:</span>
                                            <span className="font-medium text-right">{config.base_price_coins} Coins</span>
                                        </div>
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="text-muted-foreground">Rate per Coin:</span>
                                            <span className="font-medium text-right">{formatCurrency(config.base_rate, config.currency_code)}</span>
                                        </div>
                                    </div>

                                    {/* Effective Cost Highlight */}
                                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center mt-4">
                                        <p className="text-[10px] sm:text-xs text-green-700 uppercase font-bold tracking-wide mb-1">
                                            Effective Cost (Ex. GST)
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold text-green-800 flex items-baseline justify-center gap-1">
                                            {formatCurrency(calculations.effectiveCostPerLead, config.currency_code)}
                                            <span className="text-xs sm:text-sm font-normal text-green-600">/ lead</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Financial Breakdown */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Financial Breakdown</h4>
                                    <div className="space-y-3">
                                        <PriceRow 
                                            label="Base Price (Ex. GST)" 
                                            value={formatCurrency(calculations.baseCostExTax, config.currency_code)} 
                                        />
                                        
                                        {discount > 0 && (
                                            <PriceRow 
                                                label={`Discount (${discount}%)`} 
                                                value={`- ${formatCurrency(calculations.discountAmount, config.currency_code)}`}
                                                className="text-green-600"
                                            />
                                        )}

                                        <Separator className="my-2" />
                                        
                                        <PriceRow 
                                            label="Net Price (Ex. GST)" 
                                            value={formatCurrency(calculations.costAfterDiscountExTax, config.currency_code)}
                                            isBold 
                                        />
                                        
                                        <PriceRow 
                                            label={`GST (${config.tax_rate}%)`} 
                                            value={formatCurrency(calculations.taxAmount, config.currency_code)} 
                                        />

                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                            <div className="flex justify-between items-center gap-4">
                                                <span className="font-bold text-base sm:text-lg text-slate-800">Total Payable</span>
                                                <span className="font-bold text-xl sm:text-2xl text-blue-700 text-right">
                                                    {formatCurrency(calculations.finalPayable, config.currency_code)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            </Card>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesProspectCalculator;